import React, { useEffect, useState } from 'react';
import { getAllBuiltMenusByLocalIdV2 } from '../../../../../crud/menu_api/menus';
import './MenuOnline.scss';
import ErrorPage from '../../../Common/ErrorPage';
import { formatCurrency } from '../../../../../utils/functions';
import { ChevronUp, ChevronDown } from 'react-bootstrap-icons';
import { calculateMenuStatus, formatRelativeDateTime } from '../../../../Restaurant/Menu/Menu/utils/menu';
import { FaClock } from 'react-icons/fa6';

export interface BuiltMenu {
  menu_id: number;
  menu_name: string;
  menu_description: string;
  categories: Category[];
  schedule?: any; // MenuSchedule type
  state?: boolean;
}

export enum CategoryType {
  Entradas = 1,
  PlatosDeFondo = 2,
  Postres = 3,
  BebidasAlcoholicas = 4,
  BebidasNoAlcoholicas = 5,
  Acompanamientos = 6,
  Reposteria = 7,
  CafesEInfusiones = 8,
  Vinos = 9,
  Otros = 10
}

export interface Category {
  id: number;
  name: string;
  category_type_id: number | null;
  subcategories: Category[];
  items: Item[];
  position?: number | null;
}

export interface Item {
  item_id: number;
  item_name: string;
  label: string;
  price: number;
  description: string;
  main_image: string;
  image_list: string[];
  isCombo: boolean;
  image_url: string;
  category_id: number;
  selection_groups: SelectionGroup[];
  modifiers: Modifier[];
}

export interface SelectionGroup {
  selection_group_id: number;
  selection_group_name: string;
  selection_group_description: string;
  is_price_modifier: boolean;
  min_variance: number;
  max_variance: number;
  min_selections: number;
  max_selections: number;
  items: SelectionGroupItem[];
}

export interface SelectionGroupItem {
  item_id: number;
  item_name: string;
  price_modifier: number;
  min_selections: number;
  max_selections: number;
}

export interface Modifier {
  modifier_id: number;
  modifier_name: string;
  modifier_description: string;
  is_optional: boolean;
  is_price_setter: boolean;
  options: ModifierOption[];
}

export interface ModifierOption {
  option_id: number;
  option_name: string;
  option_description: string;
  price_modifier: number;
}

interface MenuOnlineProps {
  local_id: number;
}

const fallbackImage = `${process.env.PUBLIC_URL}/logos/Recurso 50MEDIOPUNTO.png`;

/**
 * Sort categories by position.
 * Categories with null/undefined position go to the end, sorted by name.
 */
const sortCategoriesByPosition = (categories: Category[]): Category[] => {
  return [...categories].sort((a, b) => {
    // Both have positions - sort by position
    if (a.position != null && b.position != null) {
      return a.position - b.position;
    }
    // Only a has position - a comes first
    if (a.position != null && b.position == null) {
      return -1;
    }
    // Only b has position - b comes first
    if (a.position == null && b.position != null) {
      return 1;
    }
    // Neither has position - sort alphabetically by name
    return a.name.localeCompare(b.name);
  }).map(cat => ({
    ...cat,
    // Also sort subcategories recursively
    subcategories: cat.subcategories ? sortCategoriesByPosition(cat.subcategories) : []
  }));
};

const MenuOnline: React.FC<MenuOnlineProps> = ({ local_id }) => {
  const [menus, setMenus] = useState<BuiltMenu[]>([]);
  const [currentMenu, setCurrentMenu] = useState<BuiltMenu | null>(null);
  const [error, setError] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  // Track which category is expanded
  const [expandedCategories, setExpandedCategories] = useState<{ [categoryId: number]: boolean }>({});
  // Track active subcategory for categories with subcategories
  const [activeSubcategory, setActiveSubcategory] = useState<{ [categoryId: number]: Category | null }>({});
  // Track which items have expanded customizations
  const [expandedItems, setExpandedItems] = useState<{ [itemId: number]: boolean }>({});


  useEffect(() => {
    if (!local_id) {
      console.error("Local ID is null");
      setError(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    getAllBuiltMenusByLocalIdV2(local_id, "Restaurant", true)
      .then((response) => {
        if (response) {
          // Filter out inactive menus (state === false)
          const activeMenus = response.data.filter((menu: BuiltMenu) => menu.state !== false);
          setMenus(activeMenus);
          if (activeMenus.length > 0) {
            setCurrentMenu(activeMenus[0]);
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching menus: ", error);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [local_id]);

  if (error) {
    return <ErrorPage />;
  }

  if (loading) {
    return (
      <div className="menu-loading-overlay">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
          </div>
          <div className="loading-text">
            <h3>Preparando tu menú</h3>
            <p>Juntando tus platos favoritos...</p>
          </div>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  // Group categories into predefined groups and sort by position within each group.
  const groupCategories = (categories: Category[]) => {
    // First, sort all categories by position (this also sorts subcategories recursively)
    const sortedCategories = sortCategoriesByPosition(categories);
    
    const paraCompartir = sortedCategories.filter(c => c.category_type_id === CategoryType.Entradas);
    const fondosYacomp = sortedCategories.filter(c =>
      c.category_type_id === CategoryType.PlatosDeFondo ||
      c.category_type_id === CategoryType.Acompanamientos
    );
    const paraBeber = sortedCategories.filter(c =>
      c.category_type_id === CategoryType.BebidasAlcoholicas ||
      c.category_type_id === CategoryType.BebidasNoAlcoholicas ||
      c.category_type_id === CategoryType.Vinos
    );
    const postres = sortedCategories.filter(c =>
      c.category_type_id === CategoryType.Postres ||
      c.category_type_id === CategoryType.Reposteria
    );
    // "Otros" includes any category not captured above.
    const otros = sortedCategories.filter(c =>
      c.category_type_id === CategoryType.Otros ||
      c.category_type_id === null ||
      (
        c.category_type_id !== CategoryType.Entradas &&
        c.category_type_id !== CategoryType.PlatosDeFondo &&
        c.category_type_id !== CategoryType.Acompanamientos &&
        c.category_type_id !== CategoryType.BebidasAlcoholicas &&
        c.category_type_id !== CategoryType.BebidasNoAlcoholicas &&
        c.category_type_id !== CategoryType.Vinos &&
        c.category_type_id !== CategoryType.Postres &&
        c.category_type_id !== CategoryType.Reposteria
      )
    );
    return { paraCompartir, fondosYacomp, paraBeber, postres, otros };
  };

  // When a category is toggled, if it has subcategories, automatically set the first subcategory as active.
  const handleToggleCategory = (category: Category) => {
    setExpandedCategories(prev => {
      const isExpanded = prev[category.id] || false;
      const newExpanded = { ...prev, [category.id]: !isExpanded };
      if (!isExpanded && category.subcategories && category.subcategories.length > 0) {
        setActiveSubcategory(prevActive => ({ ...prevActive, [category.id]: category.subcategories[0] }));
      }
      return newExpanded;
    });
  };

  const toggleItemExpansion = (itemId: number) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const renderCategoryItems = (items: Item[]) => {
    return (
      <div className="items-container">
        {items.map(item => {
          const isItemExpanded = expandedItems[item.item_id] || false;
          const hasCustomizations = (item.modifiers?.length > 0 || item.selection_groups?.length > 0);
          
          // Check if there's actually hidden content
          // For modifiers: hidden if more than 1 modifier OR first modifier has more than 3 options
          const hasHiddenModifiers = item.modifiers && item.modifiers.length > 0 && (
            item.modifiers.length > 1 || 
            item.modifiers[0].options.length > 3
          );
          // For selection groups: hidden if more than 1 group OR first group has more than 2 items
          const hasHiddenSelectionGroups = item.selection_groups && item.selection_groups.length > 0 && (
            item.selection_groups.length > 1 || 
            item.selection_groups[0].items.length > 2
          );
          const hasHiddenContent = hasHiddenModifiers || hasHiddenSelectionGroups;
          
          return (
            <div
              key={item.item_id}
              className="menu-item"
            >
              <div className="item-main-content">
                <div className="item-header">
                  <div className="item-title-section">
                    <span className="item-label">{item.label}</span>
                    {item.price !== 0 && (
                      <span className="item-price">{formatCurrency(item.price)}</span>
                    )}
                  </div>
                  <div className="item-image-container">
                    <img
                      src={item.main_image || fallbackImage}
                      alt={item.item_name || 'Sin imagen'}
                      className={`item-image${!item.main_image ? ' item-image-fallback' : ''}`}
                    />
                  </div>
                </div>
                {item.description && (
                  <div className="item-description">{item.description}</div>
                )}
                
                {/* Display modifiers and selection groups */}
                {hasCustomizations && (
                  <div className="item-customizations">
                    {/* Modifiers Section - Configurations */}
                    {item.modifiers?.length > 0 && (
                      <div className="customization-section modifier-section">
                        <div className="customization-header">
                          <span className="customization-icon">⭐</span>
                          <span className="customization-title">Agrega tus favoritos</span>
                        </div>
                        <div className="customization-items">
                          {item.modifiers.slice(0, isItemExpanded ? undefined : 1).map(modifier => (
                            <div key={modifier.modifier_id} className="customization-group">
                              <span className="customization-name">{modifier.modifier_name}</span>
                              <div className="customization-options">
                                {modifier.options.slice(0, isItemExpanded ? undefined : 3).map(option => (
                                  <span key={option.option_id} className="customization-option modifier-option">
                                    {option.option_name}
                                    {option.price_modifier > 0 && (
                                      <span className="option-price">+{formatCurrency(option.price_modifier)}</span>
                                    )}
                                  </span>
                                ))}
                                {!isItemExpanded && modifier.options.length > 3 && (
                                  <span className="customization-more">+{modifier.options.length - 3} más</span>
                                )}
                              </div>
                            </div>
                          ))}
                          {!isItemExpanded && item.modifiers.length > 1 && (
                            <div className="customization-group-more">
                              +{item.modifiers.length - 1} configuraciones más
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Selection Groups Section - Additions */}
                    {item.selection_groups?.length > 0 && (
                      <div className="customization-section selection-section">
                        <div className="customization-header">
                          <span className="customization-icon">➕</span>
                          <span className="customization-title">Elige como a ti más te gusta</span>
                        </div>
                        <div className="customization-items">
                          {item.selection_groups.slice(0, isItemExpanded ? undefined : 1).map(group => (
                            <div key={group.selection_group_id} className="customization-group">
                              <span className="customization-name">{group.selection_group_name}</span>
                              <div className="customization-options">
                                {group.items.slice(0, isItemExpanded ? undefined : 2).map(sgItem => (
                                  <span key={sgItem.item_id} className="customization-option selection-option">
                                    {sgItem.item_name}
                                    {sgItem.price_modifier > 0 && (
                                      <span className="option-price">+{formatCurrency(sgItem.price_modifier)}</span>
                                    )}
                                  </span>
                                ))}
                                {!isItemExpanded && group.items.length > 2 && (
                                  <span className="customization-more">+{group.items.length - 2} más</span>
                                )}
                              </div>
                            </div>
                          ))}
                          {!isItemExpanded && item.selection_groups.length > 1 && (
                            <div className="customization-group-more">
                              +{item.selection_groups.length - 1} adiciones más
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Show More/Less Button - Only show if there's hidden content */}
                    {hasHiddenContent && (
                      <button 
                        className="customization-toggle"
                        onClick={() => toggleItemExpansion(item.item_id)}
                      >
                        <span>{expandedItems[item.item_id] ? 'Ver menos' : 'Ver más opciones'}</span>
                        <ChevronDown 
                          className="toggle-icon" 
                          style={{ transform: expandedItems[item.item_id] ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCategory = (category: Category) => {
    const isExpanded = expandedCategories[category.id] || false;
    return (
      <div className={`category-block ${isExpanded ? 'selected' : ''}`}>
        <div className="category-header" onClick={() => handleToggleCategory(category)}>
          <span className="category-title">{category.name}</span>
          <span className="toggle-icon">
            {isExpanded ? <ChevronUp /> : <ChevronDown />}
          </span>
        </div>
        {isExpanded && (
          <>
            {category.items && category.items.length > 0 && renderCategoryItems(category.items)}
            {category.subcategories && category.subcategories.length > 0 && (
              <>
                <div className="subcategory-tabs">
                  {category.subcategories.map(sub => (
                    <div
                      key={sub.id}
                      className={`subcategory-tab ${activeSubcategory[category.id]?.id === sub.id ? 'active' : ''}`}
                      onClick={() => setActiveSubcategory(prev => ({ ...prev, [category.id]: sub }))}
                    >
                      {sub.name}
                    </div>
                  ))}
                </div>
                <div className="subcategory-items">
                  {renderCategoryItems((activeSubcategory[category.id] || category.subcategories[0]).items)}
                </div>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  const renderGroupedCategories = (header: string, categories: Category[]) => {
    if (!categories || categories.length === 0) return null;
    return (
      <div className="category-group">
        <div className="group-title-subtle">{header}</div>
        {categories.map(cat => (
          <div key={cat.id}>{renderCategory(cat)}</div>
        ))}
      </div>
    );
  };

  const groups = currentMenu ? groupCategories(currentMenu.categories) : { paraCompartir: [], fondosYacomp: [], paraBeber: [], postres: [], otros: [] };

  // Calculate menu schedule status if the menu has a schedule
  const menuStatus = currentMenu?.schedule ? calculateMenuStatus(currentMenu.schedule) : null;

  return (
    <div className="menu-online-container">
      {/* 1. Selector de Menú (Solo si hay más de 1 menú) */}
      {menus.length > 1 && (
        <div className="menu-online-menu-selector">
          {menus.map(menu => (
            <div 
              className={`menu-option ${currentMenu?.menu_id === menu.menu_id ? 'active' : ''}`} 
              key={menu.menu_id} 
              onClick={() => setCurrentMenu(menu)}
            >
              {menu.menu_name}
            </div>
          ))}
        </div>
      )}

      {/* 2. Descripción del Menú:*/}
      {currentMenu?.menu_description && (
        <div className="description-card">
          <div className="description-text">{currentMenu.menu_description}</div>
          <div className="description-separator"></div>
        </div>
      )}
      
      {/* 3. Banners de Horario */}
      {currentMenu && menuStatus && !menuStatus.isOpen && (
        <div className="menu-schedule-banner closed">
          <div className="banner-icon">
            <FaClock />
          </div>
          <div className="banner-content">
            <div className="banner-title">Esta carta está temporalmente inactiva</div>
            <div className="banner-info">
              {menuStatus.nextOpening ? (
                <>
                  Abre {formatRelativeDateTime(menuStatus.nextOpening)}
                  {menuStatus.currentDaySchedule && menuStatus.currentDaySchedule.length > 0 && (
                    <span className="schedule-range">
                      {' '}({menuStatus.currentDaySchedule[0].open} - {menuStatus.currentDaySchedule[0].close})
                    </span>
                  )}
                </>
              ) : (
                'No hay próxima apertura programada'
              )}
            </div>
          </div>
        </div>
      )}
      
      {currentMenu && menuStatus && menuStatus.isOpen && menuStatus.nextClosing && (
        <>
        <div className='menu-description-container'>
        <div className='menu-description'> {currentMenu.menu_description}</div>

        <div className="scanlines" />
        </div>
        <div className="menu-schedule-banner open">

          <div className="banner-icon">
            <FaClock />
          </div>
          <div className="banner-content">
            <div className="banner-info">
              Disponible hasta {formatRelativeDateTime(menuStatus.nextClosing)}
            </div>
          </div>
        </div>
        </>
      )}
      
      {/* 4. Contenido del Menú */}
      {currentMenu && (
        <div className="menu-online-content">
          {renderGroupedCategories("Para comenzar", groups.paraCompartir)}
          {renderGroupedCategories("Fondos y acompañamientos", groups.fondosYacomp)}
          {renderGroupedCategories("Para beber", groups.paraBeber)}
          {renderGroupedCategories("Postres", groups.postres)}
          {renderGroupedCategories("Otros", groups.otros)}
        </div>
      )}
      {/* {renderItemModal()} */}
    </div>
  );
};

export default MenuOnline;